import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { useToastManager } from "@porsche-design-system/components-react"
import type { ActionMenuView } from "@slfinrtl/ui-bff-routes"
import { useCallback, useState } from "react"
import { defineMessages, useIntl } from "react-intl"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { openListing } from "src/common/openListing/openListing"
import { addReferralToUrl } from "src/common/referralLink/addReferralToUrl"
import type { HoverableFlyoutMenuProps } from "src/components"
import { useModal } from "src/context/modal/useModal"
import type { TransferVehicleModalProps } from "src/screens/offers/common/components/TransferVehicleModal"
import { TransferVehicleModal } from "src/screens/offers/common/components/TransferVehicleModal"
import { MenuItem } from "./MenuItem"
import { hasItems } from "./hasItems"
import type { CreateOfferModalContainerProps } from "./modals/createOfferModal/CreateOfferModalContainer"
import { CreateOfferModalContainer } from "./modals/createOfferModal/CreateOfferModalContainer"
import type { CreateStockExposeModalContainerProps } from "./modals/createStockExposeModal/CreateStockExposeModalContainer"
import { CreateStockExposeModalContainer } from "./modals/createStockExposeModal/CreateStockExposeModalContainer"
import type { OpenListingModalProps } from "./modals/openListingModal/OpenListingModal"
import { OpenListingModal } from "./modals/openListingModal/OpenListingModal"
import type { PrintExposeModalContainerProps } from "./modals/printExposeModal/PrintExposeModalContainer"
import { PrintExposeModalContainer } from "./modals/printExposeModal/PrintExposeModalContainer"
import type { PrintPriceTagModalContainerProps } from "./modals/printPriceTagModal/PrintPriceTagModalContainer"
import { PrintPriceTagModalContainer } from "./modals/printPriceTagModal/PrintPriceTagModalContainer"
import type { PrintTyreLabelModalContainerProps } from "./modals/printTyreLabelModal/PrintTyreLabelModalContainer"
import { PrintTyreLabelModalContainer } from "./modals/printTyreLabelModal/PrintTyreLabelModalContainer"
import { removeNull } from "./removeNull"
import type { ActionItemKey } from "./trackingActionMap"
import type { MenuSection } from "./types/MenuSection"

const messages = defineMessages({
	copySuccess: {
		defaultMessage: "Copied Listinglink to clipboard.",
		id: "copyListingUrlToClipboard.success",
	},
})

interface UseActionMenuProps {
	actionMenu: ActionMenuView
	hideEditPricing?: boolean
	onEELClick?: () => Promise<void>
	onItemClick?: (itemKey: ActionItemKey) => void
	onMonroneyLabelClick?: () => Promise<void>
	vehicleKey: string
}

export const useActionMenu = ({
	actionMenu,
	hideEditPricing,
	onEELClick,
	onItemClick,
	onMonroneyLabelClick,
	vehicleKey,
}: UseActionMenuProps): HoverableFlyoutMenuProps["menu"] => {
	const [isCopyListingLinkLoading, setIsCopyListingLinkLoading] = useState(false)

	const showTyreModal = useModal<PrintTyreLabelModalContainerProps>(PrintTyreLabelModalContainer)
	const showPriceTagModal = useModal<PrintPriceTagModalContainerProps>(PrintPriceTagModalContainer)
	const showExposeModal = useModal<PrintExposeModalContainerProps>(PrintExposeModalContainer)
	const showCreateStockExposeModal = useModal<CreateStockExposeModalContainerProps>(CreateStockExposeModalContainer)
	const showOpenListingModal = useModal<OpenListingModalProps>(OpenListingModal)
	const showCreateOfferModal = useModal<CreateOfferModalContainerProps>(CreateOfferModalContainer)
	const showTransferModal = useModal<TransferVehicleModalProps>(TransferVehicleModal)

	const routes = useDealerAppRoutes()
	const { getReferralId, locale } = useDealerContext()
	const { formatMessage } = useIntl()
	const { addMessage } = useToastManager()

	const [isEELLoading, setIsEELLoading] = useState(false)
	const [isMonroneyLabelLoading, setIsMonroneyLabelLoading] = useState(false)
	const [isOpenListingLoading, setIsOpenListingLoading] = useState(false)

	const handleOnItemClick = useCallback(
		(itemKey: ActionItemKey) => {
			onItemClick?.(itemKey)
		},
		[onItemClick],
	)

	const onCreateOfferClick = useCallback(() => {
		const offerType = actionMenu.createOffer?.offerType
		if (offerType === "DOS") {
			handleOnItemClick("createOffer")
			showCreateOfferModal({ vehicleKey })
		}
	}, [actionMenu.createOffer?.offerType, handleOnItemClick, showCreateOfferModal, vehicleKey])

	const onOpenListingClick = useCallback(async () => {
		setIsOpenListingLoading(true)
		const referralId = await getReferralId()
		setIsOpenListingLoading(false)

		const links = actionMenu.openListing.links.map((link) => ({
			...link,
			url: addReferralToUrl(link.url, { int_id: "dealer_open_listing_referral_link", referral: referralId }),
		}))

		openListing(links, showOpenListingModal, () => handleOnItemClick("openListing"))
	}, [getReferralId, actionMenu.openListing.links, showOpenListingModal, handleOnItemClick])

	const onCopyListingLinkClick = useCallback(async () => {
		setIsCopyListingLinkLoading(true)
		const referralId = await getReferralId()
		setIsCopyListingLinkLoading(false)

		const link =
			actionMenu.copyListingLink.links.find(({ name }) => name === locale) ?? actionMenu.copyListingLink.links[0]
		const referralLink = addReferralToUrl(link.url, {
			int_id: "dealer_open_listing_referral_link",
			referral: referralId,
		})
		navigator.clipboard.writeText(referralLink)
		addMessage({
			state: "success",
			text: formatMessage(messages.copySuccess),
		})
		handleOnItemClick("copyListingLink")
	}, [getReferralId, actionMenu.copyListingLink.links, addMessage, formatMessage, handleOnItemClick, locale])

	const onOpenPFCLinkClick = useCallback(() => {
		if (!actionMenu.openPfcLink?.links || actionMenu.openPfcLink.links.length === 0) {
			return
		}
		handleOnItemClick("openPFCLink")
		if (actionMenu.openPfcLink.links.length === 1) {
			window.open(actionMenu.openPfcLink.links[0].url, "_blank")
		}
	}, [handleOnItemClick, actionMenu.openPfcLink])

	const onTransferVehicleForExternalOfferClick = useCallback(() => {
		showTransferModal({
			vehicleId: vehicleKey,
		})
	}, [showTransferModal, vehicleKey])

	const menu: MenuSection[] = [
		[
			actionMenu.details && routes?.inventoryList?.vehicleDetails ? (
				<MenuItem
					key="detailVehicle"
					data-testid="action-menu_vehicle-details"
					onClick={() => handleOnItemClick("detailVehicle")}
					route={{ to: routes.inventoryList.vehicleDetails.url(vehicleKey) }}
					{...actionMenu.details}
				/>
			) : null,
		],
		[
			routes?.inventoryList?.vehicleDetails?.reserve ? (
				<MenuItem
					key="reserveVehicle"
					data-testid="action-menu_reserve"
					onClick={() => handleOnItemClick("reserveVehicle")}
					route={{ to: routes.inventoryList.vehicleDetails.reserve.url(vehicleKey) }}
					{...actionMenu.reservation}
				/>
			) : null,
			actionMenu.openSaleDetails && routes?.salesList?.saleDetails?.url ? (
				<MenuItem
					key="openSaleDetails"
					data-testid="action-menu_openSaleDetails"
					onClick={() => handleOnItemClick("openSaleDetails")}
					route={{ to: routes.salesList.saleDetails.url(actionMenu.openSaleDetails.saleId) }}
					disabledHint={actionMenu.openSaleDetails.disabledHint}
					label={actionMenu.openSaleDetails.label}
				/>
			) : actionMenu.reportSale && routes?.offers?.addOffer?.url ? (
				<MenuItem
					key="reportSale"
					data-testid="action-menu_reportSale"
					onClick={() => handleOnItemClick("reportSale")}
					route={{ to: routes.offers.addOffer.url({ reportSale: true, vehicleId: vehicleKey }) }}
					{...actionMenu.reportSale}
				/>
			) : actionMenu.sale && routes?.inventoryList?.vehicleDetails?.sell?.url ? (
				<MenuItem
					key="sellVehicle"
					data-testid="action-menu_sell"
					onClick={() => handleOnItemClick("sellVehicle")}
					route={{ to: routes.inventoryList.vehicleDetails.sell.url(vehicleKey) }}
					{...actionMenu.sale}
				/>
			) : null,
			routes?.inventoryList?.vehicleDetails?.deliver ? (
				<MenuItem
					key="deliver"
					data-testid="action-menu_deliver"
					onClick={() => handleOnItemClick("deliver")}
					route={{ to: routes.inventoryList.vehicleDetails.deliver.url(vehicleKey) }}
					{...actionMenu.delivery}
				/>
			) : null,
		],
		[
			hideEditPricing !== true && routes?.inventoryList?.vehicleDetails ? (
				<MenuItem
					key="editPricing"
					data-testid="action-menu_edit-listing"
					onClick={() => handleOnItemClick("editPricing")}
					route={{
						state: { editStage: "pricing" },
						to: routes.inventoryList.vehicleDetails.url(vehicleKey),
					}}
					{...actionMenu.editPricing}
				/>
			) : null,
			actionMenu.editMedia && routes?.inventoryList?.vehicleDetails?.mediaEdit?.url ? (
				<MenuItem
					key="editMedia"
					data-testid="action-menu_edit-media"
					onClick={() => handleOnItemClick("editMedia")}
					route={{ to: routes.inventoryList.vehicleDetails.mediaEdit.url(vehicleKey) }}
					{...actionMenu.editMedia}
				/>
			) : null,
		],
		[
			actionMenu.createVehicleExpose ? (
				<MenuItem
					key="createStockExpose"
					data-testid="action-menu_create-stock-expose"
					onClick={() => {
						showCreateStockExposeModal({ vehicleKey })
					}}
					{...actionMenu.createVehicleExpose}
				/>
			) : null,
			actionMenu.printVehicleExpose ? (
				<MenuItem
					key="printExpose"
					data-testid="action-menu_print-expose"
					onClick={() => {
						handleOnItemClick("printExpose")
						showExposeModal({ vehicleKey })
					}}
					{...actionMenu.printVehicleExpose}
				/>
			) : null,
			actionMenu.printPriceTag ? (
				<MenuItem
					key="printPriceTag"
					data-testid="action-menu_print-pricetag"
					onClick={() => {
						handleOnItemClick("printPriceTag")
						showPriceTagModal({ vehicleKey })
					}}
					{...actionMenu.printPriceTag}
				/>
			) : null,
			actionMenu.printMonroneyLabel ? (
				<MenuItem
					key="printMonroneyLabel"
					hasNewBadge
					data-testid="action-menu_print-monroney-label"
					isLoading={isMonroneyLabelLoading}
					onClick={() => {
						handleOnItemClick("printMonroneyLabel")
						if (onMonroneyLabelClick) {
							setIsMonroneyLabelLoading(true)
							onMonroneyLabelClick().finally(() => setIsMonroneyLabelLoading(false))
						}
					}}
					{...actionMenu.printMonroneyLabel}
				/>
			) : null,
			actionMenu.printEnergyEfficiencyLabel ? (
				<MenuItem
					key="printEnergyEfficiencyLabel"
					data-testid="action-menu_print-eel"
					isLoading={isEELLoading}
					onClick={() => {
						handleOnItemClick("printEnergyEfficiencyLabel")
						if (onEELClick) {
							setIsEELLoading(true)
							onEELClick().finally(() => setIsEELLoading(false))
						}
					}}
					{...actionMenu.printEnergyEfficiencyLabel}
				/>
			) : null,
			actionMenu.printTyreInformation ? (
				<MenuItem
					key="printTyreDocuments"
					data-testid="action-menu_print-tyre-documents"
					onClick={() => {
						handleOnItemClick("printTyreDocuments")
						showTyreModal({ vehicleKey })
					}}
					{...actionMenu.printTyreInformation}
				/>
			) : null,
		],
		[
			<MenuItem
				key="copyListingLink"
				isLoading={isCopyListingLinkLoading}
				onClick={onCopyListingLinkClick}
				{...actionMenu.copyListingLink}
			/>,
			<MenuItem
				key="openListing"
				data-testid="action-menu_open-listing"
				isLoading={isOpenListingLoading}
				onClick={onOpenListingClick}
				{...actionMenu.openListing}
			/>,
			actionMenu.openPfcLink ? (
				<MenuItem
					key="openPFCLink"
					data-testid="action-menu_open-pfc-link"
					onClick={onOpenPFCLinkClick}
					{...actionMenu.openPfcLink}
				/>
			) : null,
		],
		[
			actionMenu.transfer && routes?.inventoryList?.vehicleDetails?.transfer ? (
				<MenuItem
					key="transferVehicle"
					data-testid="action-menu_transfer"
					onClick={() => handleOnItemClick("transferVehicle")}
					route={{ to: routes.inventoryList.vehicleDetails.transfer.url(vehicleKey) }}
					{...actionMenu.transfer}
				/>
			) : null,
			routes?.inventoryList?.vehicleDetails?.remove ? (
				<MenuItem
					key="removeVehicle"
					data-testid="action-menu_delete"
					onClick={() => handleOnItemClick("removeVehicle")}
					route={{ to: routes.inventoryList.vehicleDetails.remove.url(vehicleKey) }}
					{...actionMenu.removeVehicle}
				/>
			) : null,
		],
		[
			actionMenu.transferVehicleForExternalOffer ? (
				<MenuItem
					key="transferVehicleForExternalOffer"
					data-testid="action-menu_transfer-vehicle-for-external-offer"
					onClick={onTransferVehicleForExternalOfferClick}
					{...actionMenu.transferVehicleForExternalOffer}
				/>
			) : null,
		],
		[
			actionMenu.createOffer && actionMenu.createOffer?.offerType === "DOS" ? (
				<MenuItem
					key="createOffer"
					data-testid="action-menu_createOffer"
					onClick={onCreateOfferClick}
					{...actionMenu.createOffer}
				/>
			) : null,
			actionMenu.createOffer &&
			actionMenu.createOffer?.offerType === "OFFER_SERVICE" &&
			routes?.offers?.addOffer?.url ? (
				<MenuItem
					key="createOffer_offer_service"
					data-testid="action-menu_createOffer_offer_service"
					onClick={() => handleOnItemClick("createOffer")}
					route={{ to: routes.offers.addOffer.url({ vehicleId: vehicleKey }) }}
					{...actionMenu.createOffer}
				/>
			) : null,
			actionMenu.offerOverview && routes?.inventoryList?.vehicleDetails?.sales?.url ? (
				<MenuItem
					key="offerOverview"
					data-testid="action-menu_offerOverview"
					route={{ to: routes.inventoryList.vehicleDetails.sales.url(vehicleKey) }}
					{...actionMenu.offerOverview}
					onClick={() => handleOnItemClick("offerOverview")}
				/>
			) : null,
		],
	]

	return menu.map(removeNull).filter(hasItems)
}
